<script lang="ts">
  import { onMount } from 'svelte'
  import Papa from 'papaparse'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError, showError } from '$lib/util/errors'

  type PropertyManager =
    paths['/property-managers']['get']['responses'][200]['content']['application/json']['items'][number]
  type AuditRun =
    paths['/property-managers/{pmId}/audit-runs']['post']['responses'][200]['content']['application/json']
  type Finding = AuditRun['findings'][number]
  type AuditRunSummary =
    paths['/property-managers/{pmId}/audit-runs']['get']['responses'][200]['content']['application/json']['items'][number]

  let pmOptions = $state<PropertyManager[]>([])
  let pmId = $state<number | null>(null)
  let propertyId = $state('')
  let perPetRate = $state('')
  let retainImport = $state(false)

  let headers = $state<string[]>([])
  let parsedRows = $state<Record<string, string>[]>([])
  let addressCol = $state('')
  let unitCol = $state('')
  let chargeCol = $state('')

  let running = $state(false)
  let result = $state<AuditRun | null>(null)

  let pastRuns = $state<AuditRunSummary[]>([])
  let loadingHistory = $state(false)

  const dollars = (cents: number | null): string =>
    cents === null
      ? ''
      : cents < 0
        ? '-$' + (-cents / 100).toFixed(2)
        : '$' + (cents / 100).toFixed(2)

  async function loadPMOptions(): Promise<void> {
    const { data, error } = await api.GET('/property-managers', {
      params: { query: { limit: 50 } },
    })
    if (error) {
      showApiError(error)
      return
    }
    pmOptions = data?.items ?? []
  }

  async function loadHistory(id: number): Promise<void> {
    loadingHistory = true
    const { data, error } = await api.GET('/property-managers/{pmId}/audit-runs', {
      params: { path: { pmId: id } },
    })
    loadingHistory = false
    if (error) {
      showApiError(error)
      return
    }
    pastRuns = data?.items ?? []
  }

  async function viewRun(id: number): Promise<void> {
    const { data, error } = await api.GET('/audit-runs/{id}', { params: { path: { id } } })
    if (error || !data) {
      showApiError(error)
      return
    }
    result = data
  }

  // Reload the run history whenever a property manager is selected (and after a new run).
  $effect(() => {
    const id = pmId
    if (id === null) {
      pastRuns = []
      return
    }
    void loadHistory(id)
  })

  function pickDefault(fields: string[], needle: string): string {
    return fields.find((f) => f.toLowerCase().includes(needle)) ?? ''
  }

  function onFile(e: Event): void {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file) return
    // A new upload invalidates any prior run and mapping state.
    result = null
    headers = []
    parsedRows = []
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const firstError = res.errors?.[0]
        if (firstError) {
          showError({ detail: `Could not read that CSV: ${firstError.message}` })
          return
        }
        const fields = res.meta.fields ?? []
        if (fields.length === 0) {
          showError({
            detail: 'That file has no readable columns. Check that the first row is a header.',
          })
          return
        }
        headers = fields
        parsedRows = res.data
        addressCol = pickDefault(fields, 'address')
        unitCol = pickDefault(fields, 'unit')
        chargeCol = pickDefault(fields, 'pet')
      },
      error: (err) => showError({ detail: `Could not read that CSV: ${err.message}` }),
    })
  }

  function parseCents(raw: string | undefined): number | null {
    if (!raw) return null
    const n = Number(raw.replace(/[$,\s]/g, ''))
    if (Number.isNaN(n) || n < 0) return null
    return Math.round(n * 100)
  }

  async function runAudit(): Promise<void> {
    if (pmId === null) {
      showError({ detail: 'Select a property manager.' })
      return
    }
    if (!addressCol) {
      showError({ detail: 'Map the address column.' })
      return
    }
    const rows = parsedRows
      .map((r) => {
        const address = (r[addressCol] ?? '').trim()
        const unit = unitCol ? (r[unitCol] ?? '').trim() : ''
        return {
          label: address + (unit ? ` ${unit}` : ''),
          address,
          unit: unit || null,
          petRentCents: parseCents(chargeCol ? r[chargeCol] : undefined),
        }
      })
      .filter((r) => r.address !== '')
    if (rows.length === 0) {
      showError({ detail: 'No rows with an address to audit.' })
      return
    }
    // FormField binds strings; empty means "not provided", never 0.
    const rateDollars = perPetRate.trim() === '' ? null : Number(perPetRate)
    const perPetRateCents =
      rateDollars === null || Number.isNaN(rateDollars) ? null : Math.round(rateDollars * 100)
    const propertyIdValue = propertyId.trim() === '' ? null : Number(propertyId)

    running = true
    const { data, error } = await api.POST('/property-managers/{pmId}/audit-runs', {
      params: { path: { pmId } },
      body: {
        ...(propertyIdValue !== null && !Number.isNaN(propertyIdValue)
          ? { propertyId: propertyIdValue }
          : {}),
        perPetRateCents,
        retainImport,
        rows,
      },
    })
    running = false
    if (error || !data) {
      showApiError(error)
      return
    }
    result = data
    // Surface the just-created run in the history list.
    if (pmId !== null) void loadHistory(pmId)
  }

  interface FlatFinding {
    id: number
    csvLabel: string
    verifiedPets: number
    petRent: string
    gap: string
    status: string
    reason: string
  }

  const findingRows = (findings: Finding[]): FlatFinding[] =>
    findings.map((f, i) => ({
      id: i,
      csvLabel: f.csvLabel,
      verifiedPets: f.verifiedPets,
      petRent: dollars(f.petRentCents),
      gap: dollars(f.gapCents),
      status: f.status,
      reason: f.reason ?? '',
    }))

  onMount(() => void loadPMOptions())
</script>

<div class="section-header">
  <h1>Revenue audit</h1>
</div>

<div class="form-field">
  <label for="pm">Property manager *</label>
  <select id="pm" bind:value={pmId}>
    <option value={null}>- Select -</option>
    {#each pmOptions as pm (pm.id)}
      <option value={pm.id}>{pm.name}</option>
    {/each}
  </select>
</div>

<FormField
  label="Property ID (optional)"
  name="propertyId"
  type="number"
  bind:value={propertyId}
  placeholder="e.g. 42"
/>

<div class="form-field">
  <label for="csv">Rent roll CSV *</label>
  <input id="csv" type="file" accept=".csv" onchange={onFile} />
</div>

{#if headers.length > 0}
  <p class="muted">{parsedRows.length} rows parsed. Map the columns:</p>
  <div class="form-field">
    <label for="addressCol">Address column *</label>
    <select id="addressCol" bind:value={addressCol} onchange={() => (result = null)}>
      <option value="">- Select -</option>
      {#each headers as h (h)}
        <option value={h}>{h}</option>
      {/each}
    </select>
  </div>
  <div class="form-field">
    <label for="unitCol">Unit number column</label>
    <select id="unitCol" bind:value={unitCol} onchange={() => (result = null)}>
      <option value="">- None -</option>
      {#each headers as h (h)}
        <option value={h}>{h}</option>
      {/each}
    </select>
  </div>
  <div class="form-field">
    <label for="chargeCol">Pet rent charged column</label>
    <select id="chargeCol" bind:value={chargeCol} onchange={() => (result = null)}>
      <option value="">- None -</option>
      {#each headers as h (h)}
        <option value={h}>{h}</option>
      {/each}
    </select>
  </div>
{/if}

<FormField
  label="Per-pet monthly rate in dollars (optional)"
  name="perPetRate"
  type="number"
  bind:value={perPetRate}
  placeholder="e.g. 35.00"
/>

<label class="checkbox">
  <input type="checkbox" bind:checked={retainImport} />
  Retain this import
</label>

<div>
  <button type="button" class="primary" onclick={runAudit} disabled={running}>
    {running ? 'Running…' : 'Run audit'}
  </button>
</div>

{#if result}
  <div class="results">
    <div class="headline">
      Estimated monthly leak: <strong>{dollars(result.estLeakCents)}</strong>
    </div>
    <div class="counts">
      {result.rowCount} rows · {result.matchedCount} matched · {result.gapCount} gaps
    </div>

    {#if result.propertyId === null && result.notOnRollCount > 0}
      <p class="hint">
        {result.notOnRollCount} verified pet{result.notOnRollCount === 1 ? '' : 's'} on units not found in this
        roll. This audit covers every unit for the property manager, so a partial upload
        is not counted as leaked revenue. Scope to a single property, or upload a roll
        that covers these units, to include them.
      </p>
    {/if}

    {#if result.findings.length === 0 && result.matchedCount === 0}
      <p class="muted">No verified pets to reconcile yet.</p>
    {:else}
      <Table
        items={findingRows(result.findings)}
        columns={[
          { key: 'csvLabel', label: 'CSV label' },
          { key: 'verifiedPets', label: 'Verified pets' },
          { key: 'petRent', label: 'Pet rent' },
          { key: 'gap', label: 'Gap' },
          { key: 'status', label: 'Status' },
          { key: 'reason', label: 'Reason' },
        ]}
      />
    {/if}
  </div>
{/if}

{#if pmId !== null}
  <section class="history">
    <h2>
      Past runs
      <!-- Mirrors RUN_HISTORY_LIMIT on the server; surfaced so the cap is not silent. -->
      {#if pastRuns.length >= 100}<span class="muted">(most recent 100)</span>{/if}
    </h2>
    {#if loadingHistory}
      <p class="muted">Loading…</p>
    {:else if pastRuns.length === 0}
      <p class="muted">No audits run for this property manager yet.</p>
    {:else}
      <table class="runs">
        <thead>
          <tr>
            <th>Date</th>
            <th>Scope</th>
            <th class="num">Rows</th>
            <th class="num">Matched</th>
            <th class="num">Gaps</th>
            <th class="num">Est. leak</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each pastRuns as run (run.id)}
            <tr>
              <td>{new Date(run.createdAt).toLocaleString()}</td>
              <td>{run.propertyId === null ? 'All units' : `Property ${run.propertyId}`}</td>
              <td class="num">{run.rowCount}</td>
              <td class="num">{run.matchedCount}</td>
              <td class="num">{run.gapCount}</td>
              <td class="num">{dollars(run.estLeakCents)}</td>
              <td class="num">
                <button type="button" class="link" onclick={() => viewRun(run.id)}>View</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </section>
{/if}

<style>
  .section-header {
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
    max-width: 420px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field select,
  .form-field input {
    padding: 6px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
  .checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    margin-bottom: 16px;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.primary:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .muted {
    color: var(--muted);
    font-size: 0.875rem;
  }
  .hint {
    max-width: 560px;
    font-size: 0.875rem;
    color: var(--ink);
    background: var(--surface-2, rgba(0, 0, 0, 0.04));
    border-left: 3px solid var(--brand);
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 16px;
  }
  .results {
    margin-top: 24px;
  }
  .headline {
    font-size: 1.25rem;
    margin-bottom: 4px;
  }
  .counts {
    color: var(--muted);
    font-size: 0.875rem;
    margin-bottom: 16px;
  }
  .history {
    margin-top: 32px;
    border-top: 1px solid var(--line);
    padding-top: 16px;
  }
  .history h2 {
    font-size: 1.1rem;
    margin: 0 0 12px;
  }
  table.runs {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  table.runs th,
  table.runs td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    text-align: left;
  }
  table.runs th {
    font-weight: 600;
    color: var(--ink);
  }
  table.runs .num {
    text-align: right;
    white-space: nowrap;
  }
  button.link {
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0;
    text-decoration: underline;
  }
</style>
